import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Briefcase,
    Plus,
    Edit,
    Trash2,
    CheckCircle,
    X,
    Calendar,
    MapPin,
} from 'lucide-react';
import Swal from 'sweetalert2';
import {
    fetchOrdreMissionRequest,
    createOrdreMissionRequest,
    updateOrdreMissionRequest,
    deleteOrdreMissionRequest,
} from '../../../../redux/actions/ordreMissionActions';
import '../parametresStyles/ParamConges.css';

const ParamMissions = () => {
    const dispatch = useDispatch();
    const { ordresMission, loading, error } = useSelector((state) => state.ordreMission || {
        ordresMission: [],
        loading: false,
        error: null,
    });

    const [showModal, setShowModal] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [activeTab, setActiveTab] = useState("missions");
    const [formData, setFormData] = useState({
        code: '',
        libelle: '',
        description: '',
        dateDebut: '',
        dateFin: '',
        lieu: '',
        centreCout: '',
        workflow: 'manager',
        active: true,
    });

    const workflowOptions = [
        { value: 'manager', label: 'Manager' },
        { value: 'admin', label: 'Admin RH' },
        { value: 'both', label: 'Les deux' },
    ];

    useEffect(() => {
        dispatch(fetchOrdreMissionRequest());
    }, [dispatch]);

    const tabs = useMemo(
        () => [
            { id: "missions", label: "Ordres de Mission", icon: Briefcase },
            { id: "rapports", label: "Rapports", icon: Calendar },
        ],
        []
    );

    const openModal = (mission = null) => {
        if (mission) {
            setEditItem(mission);
            setFormData({
                code: mission.code || '',
                libelle: mission.libelle || '',
                description: mission.description || '',
                dateDebut: mission.dateDebut || '',
                dateFin: mission.dateFin || '',
                lieu: mission.lieu || '',
                centreCout: mission.centreCout || '',
                workflow: mission.workflow || 'manager',
                active: mission.active ?? true,
            });
        } else {
            setEditItem(null);
            setFormData({
                code: '',
                libelle: '',
                description: '',
                dateDebut: '',
                dateFin: '',
                lieu: '',
                centreCout: '',
                workflow: 'manager',
                active: true,
            });
        }
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditItem(null);
        setFormData({
            code: '',
            libelle: '',
            description: '',
            dateDebut: '',
            dateFin: '',
            lieu: '',
            centreCout: '',
            workflow: 'manager',
            active: true,
        });
    };

    const handleChange = (e) => {
        const { name, value, checked, type } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value,
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!formData.code.trim() || !formData.libelle.trim()) {
            Swal.fire({
                icon: 'error',
                title: 'Erreur',
                text: 'Le code et le libellé sont obligatoires',
                confirmButtonColor: '#204385',
            });
            return;
        }

        if (!formData.dateDebut || !formData.dateFin) {
            Swal.fire({
                icon: 'error',
                title: 'Erreur',
                text: 'Les dates de début et de fin sont obligatoires',
                confirmButtonColor: '#204385',
            });
            return;
        }

        if (new Date(formData.dateFin) < new Date(formData.dateDebut)) {
            Swal.fire({
                icon: 'error',
                title: 'Erreur',
                text: 'La date de fin doit être postérieure ou égale à la date de début',
                confirmButtonColor: '#204385',
            });
            return;
        }

        const payload = {
            ...formData,
        };

        if (editItem) {
            dispatch(updateOrdreMissionRequest(editItem.id, payload));
        } else {
            dispatch(createOrdreMissionRequest(payload));
        }
        closeModal();
    };

    const handleDelete = (id) => {
        Swal.fire({
            title: 'Confirmation',
            text: 'Voulez-vous vraiment supprimer cet ordre de mission ?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ee645f',
            cancelButtonColor: '#204385',
            confirmButtonText: 'Oui, supprimer',
            cancelButtonText: 'Annuler',
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(deleteOrdreMissionRequest(id));
            }
        });
    };

    const toggleActive = (mission) => {
        dispatch(
            updateOrdreMissionRequest(mission.id, {
                ...mission,
                active: !mission.active,
            })
        );
    };

    const getWorkflowLabel = (workflow) => {
        const option = workflowOptions.find(opt => opt.value === workflow);
        return option ? option.label : workflow;
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR');
    };

    const renderModal = () => {
        if (!showModal) return null;
        const isEdit = !!editItem;

        return (
            <div className="pcModal">
                <div className="pcModal__panel" role="dialog" aria-modal="true">
                    <div className="pcModal__header">
                        <div className="pcModal__title">
                            {isEdit ? 'Modifier un ordre de mission' : 'Ajouter un ordre de mission'}
                        </div>
                        <button className="pcIconBtn" onClick={closeModal} type="button" aria-label="Fermer" disabled={loading}>
                            <X size={18} />
                        </button>
                    </div>

                    <div className="pcModal__body">
                        <div className="pcForm">
                            <div className="pcField">
                                <label>Code *</label>
                                <input
                                    type="text"
                                    name="code"
                                    value={formData.code}
                                    onChange={handleChange}
                                    required
                                    disabled={loading}
                                    placeholder="Ex: MISS-001"
                                />
                            </div>

                            <div className="pcField">
                                <label>Libellé *</label>
                                <input
                                    type="text"
                                    name="libelle"
                                    value={formData.libelle}
                                    onChange={handleChange}
                                    required
                                    disabled={loading}
                                    placeholder="Ex: Mission commerciale Paris"
                                />
                            </div>

                            <div className="pcField">
                                <label>Description</label>
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    disabled={loading}
                                    rows={3}
                                    placeholder="Description de la mission..."
                                />
                            </div>

                            <div className="pcField">
                                <label>Date de début *</label>
                                <input
                                    type="date"
                                    name="dateDebut"
                                    value={formData.dateDebut}
                                    onChange={handleChange}
                                    required
                                    disabled={loading}
                                />
                            </div>

                            <div className="pcField">
                                <label>Date de fin *</label>
                                <input
                                    type="date"
                                    name="dateFin"
                                    value={formData.dateFin}
                                    onChange={handleChange}
                                    required
                                    disabled={loading}
                                />
                            </div>

                            <div className="pcField">
                                <label>Lieu</label>
                                <input
                                    type="text"
                                    name="lieu"
                                    value={formData.lieu}
                                    onChange={handleChange}
                                    disabled={loading}
                                    placeholder="Ex: Paris, France"
                                />
                            </div>

                            <div className="pcField">
                                <label>Centre de Coûts</label>
                                <input
                                    type="text"
                                    name="centreCout"
                                    value={formData.centreCout}
                                    onChange={handleChange}
                                    disabled={loading}
                                />
                            </div>

                            <div className="pcField">
                                <label>Niveau de validation *</label>
                                <div className="pcRadioGroup">
                                    {workflowOptions.map((option) => (
                                        <label key={option.value} className="pcRadioLabel">
                                            <input
                                                type="radio"
                                                name="workflow"
                                                value={option.value}
                                                checked={formData.workflow === option.value}
                                                onChange={handleChange}
                                                disabled={loading}
                                            />
                                            <span>{option.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {isEdit && (
                                <div className="pcField">
                                    <label>Actif</label>
                                    <div className="pcCheckRow">
                                        <input
                                            type="checkbox"
                                            name="active"
                                            checked={formData.active}
                                            onChange={handleChange}
                                            disabled={loading}
                                        />
                                        <span>Oui</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="pcModal__footer">
                            <button className="pcBtn pcBtn--ghost" type="button" onClick={closeModal} disabled={loading}>
                                Annuler
                            </button>
                            <button className="pcBtn pcBtn--primary" type="button" onClick={handleSubmit} disabled={loading}>
                                {isEdit ? 'Enregistrer' : 'Ajouter une mission'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="pcPage">
            <div className="pcHeader">
                <div>
                    <h1>Gestion des ordres de mission</h1>
                    <p>Configurez les types de missions et leurs paramètres.</p>
                    {error && <div style={{ marginTop: 8, color: '#b91c1c', fontWeight: 700 }}>Erreur: {error}</div>}
                </div>
            </div>

            <div className="pcWrap">
                <div className="pcCard">
                    <div className="pcTabs">
                        {tabs.map((tab) => {
                            const Icon = tab.icon;
                            return (
                                <button
                                    key={tab.id}
                                    className={`pcTab ${activeTab === tab.id ? "is-active" : ""}`}
                                    onClick={() => setActiveTab(tab.id)}
                                    type="button"
                                >
                                    <Icon size={16} />
                                    {tab.label}
                                </button>
                            );
                        })}
                    </div>

                    <div className="pcBody">
                        {/* ORDRES DE MISSION */}
                        {activeTab === "missions" && (
                            <div>
                                <div className="pcTopRow">
                                    <h2 className="pcTitle">Types de missions paramétrables</h2>
                                    <button
                                        className="pcBtn pcBtn--primary"
                                        type="button"
                                        onClick={() => openModal()}
                                        disabled={loading}
                                    >
                                        <Plus size={16} /> Ajouter une mission
                                    </button>
                                </div>

                                <div className="pcTableWrap">
                                    <table className="pcTable">
                                        <thead>
                                        <tr>
                                            <th>Code</th>
                                            <th>Libellé</th>
                                            <th>Description</th>
                                            <th>Date début</th>
                                            <th>Date fin</th>
                                            <th>Lieu</th>
                                            {/*<th>Centre de Coûts</th>*/}
                                            {/*<th>Validation</th>*/}
                                            <th>Statut</th>
                                            <th className="is-right">Actions</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan={10} className="pcEmptyCell">
                                                    Chargement...
                                                </td>
                                            </tr>
                                        ) : (ordresMission || []).length === 0 ? (
                                            <tr>
                                                <td colSpan={10} className="pcEmptyCell">
                                                    Aucun ordre de mission configuré
                                                </td>
                                            </tr>
                                        ) : (
                                            (ordresMission || []).map((mission) => (
                                                <tr key={mission.id}>
                                                    <td>
                                                        <span className="pcCode" style={{ backgroundColor: '#204385' }}>
                                                            {mission.code}
                                                        </span>
                                                    </td>
                                                    <td className="pcStrong">{mission.libelle}</td>
                                                    <td className="pcMuted">
                                                        {mission.description ?
                                                            (mission.description.length > 50 ?
                                                                    mission.description.substring(0, 50) + '...' :
                                                                    mission.description
                                                            ) :
                                                            '-'
                                                        }
                                                    </td>
                                                    <td>
                                                        <span className="pcInline">
                                                            <Calendar size={14} className="pcAccent" />
                                                            {formatDate(mission.dateDebut)}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className="pcInline">
                                                            <Calendar size={14} className="pcAccent" />
                                                            {formatDate(mission.dateFin)}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {mission.lieu ? (
                                                            <span className="pcInline">
                                                                <MapPin size={14} className="pcAccent" />
                                                                {mission.lieu}
                                                            </span>
                                                        ) : (
                                                            <span className="pcMuted">-</span>
                                                        )}
                                                    </td>
                                                    {/*<td className="pcMuted">{mission.centreCout || '-'}</td>*/}
                                                    {/*<td>*/}
                                                    {/*    <span className="pcBadge">*/}
                                                    {/*        {getWorkflowLabel(mission.workflow)}*/}
                                                    {/*    </span>*/}
                                                    {/*</td>*/}
                                                    <td>
                                                        <button
                                                            className={`pcPill ${mission.active ? 'is-on' : 'is-off'}`}
                                                            type="button"
                                                            onClick={() => toggleActive(mission)}
                                                            disabled={loading}
                                                        >
                                                            {mission.active ? 'Actif' : 'Inactif'}
                                                        </button>
                                                    </td>
                                                    <td className="is-right">
                                                        <div className="pcRowActions">
                                                            <button
                                                                className="pcIconBtn"
                                                                type="button"
                                                                title="Modifier"
                                                                onClick={() => openModal(mission)}
                                                                disabled={loading}
                                                            >
                                                                <Edit size={16} />
                                                            </button>
                                                            <button
                                                                className="pcIconBtn pcIconBtn--danger"
                                                                type="button"
                                                                title="Supprimer"
                                                                onClick={() => handleDelete(mission.id)}
                                                                disabled={loading}
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* RAPPORTS */}
                        {activeTab === "rapports" && (
                            <div>
                                <div className="pcTopRow">
                                    <h2 className="pcTitle">Rapports de missions</h2>
                                </div>

                                <div className="pcEmptyBox">
                                    Section des rapports à venir...
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {renderModal()}
        </div>
    );
};

export default ParamMissions;